/* MODULAR GRID ANIMATION — shared 2D-canvas engine.
   Vocabulary:
   - faint full grid with brighter "+" marks at some intersections (slow pulse)
   - lone small squares that hop cell-to-cell
   - the signature morph: dot → line → square outline → 3x3 dot matrix → dissolve
   - occasional grid-edge "line draws" between two intersections
   Cheap by design: a handful of entities, canvas 2D, dpr cap 2, RAF paused
   off-screen / hidden tab; static frame for reduced-motion + Shopify editor.
   Usage: <canvas data-ab-modgrid data-bg="#0034d3" data-ink="#FFFFFF"
                  data-cell="110" data-speed="1" data-density="1" [data-static]>
*/
(function () {
  if (window.__abModGrid) return;
  window.__abModGrid = true;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var EDITOR = !!(window.Shopify && window.Shopify.designMode);

  function hexToRgb(hex) {
    hex = (hex || '#FFFFFF').replace('#', '');
    if (hex.length === 3) hex = hex.replace(/(.)/g, '$1$1');
    var n = parseInt(hex, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function rgba(rgb, a) { return 'rgba(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ',' + a + ')'; }
  function rand(a, b) { return a + Math.random() * (b - a); }
  function pick(arr) { return arr[(Math.random() * arr.length) | 0]; }
  function easeInOut(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }

  function setup(cv) {
    cv.dataset.abInit = '1';
    var ctx = cv.getContext('2d');
    var bg = hexToRgb(cv.dataset.bg || '#0034d3');
    var ink = hexToRgb(cv.dataset.ink || '#FFFFFF');
    var autoCell = cv.dataset.cell === 'auto'; // fit: size cells so the full 5-wide/5-tall structure + padding always fits
    var cellBase = parseFloat(cv.dataset.cell) || 110;
    var speed = parseFloat(cv.dataset.speed) || 1;
    var density = parseFloat(cv.dataset.density) || 1;
    var mode = cv.dataset.mode || 'morph'; // 'morph' | 'stack' (panel builder)
    var anchorOpt = cv.dataset.anchor || 'auto'; // stack placement: left | center | right | auto(random)
    var scrollBuild = cv.hasAttribute('data-scrollbuild'); // tetris-style: the FIRST build is scrubbed by scroll
    var isStatic = cv.hasAttribute('data-static') || REDUCE || EDITOR;

    // scroll progress of the canvas through the viewport: 0 = just entering at the
    // bottom, 1 = (almost) fully on screen — the build completes as it scrolls in
    function scrollP() {
      var r = cv.getBoundingClientRect();
      var vh = window.innerHeight || 1;
      var denom = Math.min(r.height * 0.9, vh * 0.8);
      return Math.max(0, Math.min(1, (vh - r.top) / Math.max(60, denom)));
    }

    var W = 0, H = 0, dpr = 1, cell = cellBase, cols = 0, rows = 0;
    var crosses = [], squares = [], morphs = [], lineDraws = [], structures = [];
    var running = false, visible = false, raf = 0, t0 = performance.now();

    function resize() {
      var r = cv.parentElement.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = r.width; H = r.height;
      cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
      cv.style.width = W + 'px'; cv.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (autoCell) {
        // fit: ≥7.5 columns and ≥6 rows visible at any viewport, so the
        // 5-wide / 5-tall structure always shows complete with padding around it
        cell = Math.max(24, Math.min(96, Math.min(W / 7.5, H / 6)));
      } else {
        cell = W < 600 ? cellBase * 0.72 : cellBase;
      }
      cols = Math.ceil(W / cell) + 1;
      rows = Math.ceil(H / cell) + 1;
      buildGridLayer();
      seed();
      if (isStatic) draw(0);
    }

    function seed() {
      crosses = []; squares = []; morphs = []; lineDraws = []; structures = [];
      if (mode === 'stack') {
        // stacking mode: fewer crosses for context, no lines/morphs/wanderers —
        // ONE kit of panels drops in, then keeps shuffling into new setups
        var nCs = Math.round(cols * rows * 0.05 * density);
        for (var c = 0; c < nCs; c++) crosses.push(newCross(true));
        structures.push(newKitStructure());
        return;
      }
      var nC = Math.round(cols * rows * 0.10 * density);
      for (var i = 0; i < nC; i++) crosses.push(newCross(true));
      var nS = Math.max(2, Math.round(cols * rows * 0.02 * density));
      for (var j = 0; j < nS; j++) squares.push(newSquare(true));
      morphs.push(newMorph(true));
      if (cols * rows > 24) morphs.push(newMorph(true));
    }

    /* ── STACK MODE (tetris-builder vocabulary, blue/white) ──
       ONE fixed kit of panels (solid squares, 4x4 grid panels, half panels,
       two corner triangles — every panel with corner dots) drops in once,
       tetris-style. Then, on a timer, THE SAME PIECES glide across the plane
       into a new packed arrangement — "same parts, new layout". Connector
       octagons + hung accessory squares fade out for the shuffle and re-pop
       at the new junctions once the pieces settle.
       Placement is clamped to FULLY VISIBLE rows/columns (no bottom cut-off). */
    function visGrid() {
      return { vc: Math.max(2, Math.floor(W / cell)), vr: Math.max(2, Math.floor(H / cell)) };
    }

    function newKitStructure() {
      var vg = visGrid();
      // STRUCTURE RULE: structures are 3-5 columns wide and 2-5 panels tall —
      // kit size targets a 3x3-or-bigger footprint wherever the canvas allows
      var hMax = Math.min(5, vg.vr);
      var wMax = Math.min(5, vg.vc);
      var capacity = wMax * hMax;
      var N = Math.min(capacity, 9 + ((Math.random() * 4) | 0)); // aim 9-12 pieces
      if (capacity < 9) N = Math.max(4, capacity);               // tiny canvas fallback
      var pieces = [];
      // guaranteed kit core: BOTH triangles + a half panel are always present
      // (extras fill in with squares/grids/another half)
      var types = ['grid', 'fill', 'triR', 'half', 'triL'];
      while (types.length < N) types.push(pick(['grid', 'fill', 'grid', 'half']));
      types = types.slice(0, N);
      for (var i = 0; i < N; i++) {
        pieces.push({ type: types[i], cx: 0, cy: 0, fx: 0, fy: 0, prog: 1, delay: 0, k: 0 });
      }
      var st = { pieces: pieces, conns: [], acc: [], state: 'drop', clock: 0,
                 dropDur: 1400, glideDur: 950, popDur: 900, /* slower pop = gradual reveal */
                 accShown: false,
                 interval: parseFloat(cv.dataset.interval) || 3000 };
      arrange(st, true);
      // accessories are PERSISTENT (one per grid panel +/- one extra): they ride
      // their host panel through every shuffle instead of re-rolling each time
      for (var ap = 0; ap < st.pieces.length; ap++) {
        if (st.pieces[ap].type !== 'grid') continue;
        st.acc.push({ piece: ap, ix: 1 + ((Math.random() * 3) | 0), iy: 1 + ((Math.random() * 3) | 0), k: Math.random() });
        if (Math.random() < 0.4) st.acc.push({ piece: ap, ix: 1 + ((Math.random() * 3) | 0), iy: 1 + ((Math.random() * 3) | 0), k: Math.random() });
      }
      if (isStatic) { st.state = 'idle'; st.clock = 0; st.connP = 1; st.accP = 1; st.accShown = true; }
      else { st.connP = 0; st.accP = 0; }
      return st;
    }

    function arrange(st, first) {
      var vg = visGrid();
      var N = st.pieces.length;
      var hMax = Math.min(5, vg.vr);
      var yBot = vg.vr - 1;                          // last FULLY visible row
      /* STRUCTURE RULES:
         - footprint 3-5 columns wide (canvas permitting)
         - every column at least 2 panels tall, tallest at least 3 → reads as a
           wall block, not a clump
         - GRAVITY: columns are contiguous stacks rising from the bottom row,
           which is always full across the footprint — nothing ever floats */
      var hLo = hMax >= 2 ? 2 : 1;
      var wMin = Math.min(3, vg.vc), wMax = Math.min(5, vg.vc);
      var ws = [], wsStrict = [];
      for (var w = wMin; w <= wMax; w++) {
        if (N >= hLo * w && N <= hMax * w) {
          ws.push(w);
          if (N > hLo * w) wsStrict.push(w); // remainder left over → a ≥3 column emerges naturally
        }
      }
      if (wsStrict.length) ws = wsStrict;
      if (!ws.length) { // fallback for tiny canvases / odd N: widest that still fills
        for (var w2 = wMax; w2 >= 2; w2--) if (N >= w2) { ws.push(w2); break; }
        if (!ws.length) ws.push(Math.max(1, Math.min(2, vg.vc)));
      }
      var wW = pick(ws);
      // base every column at hLo, sprinkle the remainder (cap hMax) → stepped silhouettes
      var hs = [], left = N, g = 0;
      var base = Math.min(hLo, Math.floor(N / wW));
      for (var i = 0; i < wW; i++) { hs.push(base); left -= base; }
      while (left > 0 && g++ < 120) {
        var ci = (Math.random() * wW) | 0;
        if (hs[ci] < hMax) { hs[ci]++; left--; }
      }
      // rule: tallest column ≥ 3 (when the canvas allows). With strict
      // widths a remainder guarantees this; this is a belt-and-braces shuffle
      // that never drops a column below the 2-panel grounded base.
      if (hMax >= 3) {
        var hiIdx = 0, loIdx = wW - 1;
        for (var h1 = 0; h1 < wW; h1++) {
          if (hs[h1] > hs[hiIdx]) hiIdx = h1;
          if (hs[h1] <= hs[loIdx]) loIdx = h1;
        }
        if (hs[hiIdx] < 3 && hs[loIdx] > hLo && hiIdx !== loIdx) { hs[loIdx]--; hs[hiIdx]++; }
      }
      // horizontal placement: anchored left/centre/right (one-cell padding from
      // the edge) or random for the bento tiles
      var span = Math.max(0, vg.vc - wW);
      var x0;
      if (anchorOpt === 'left') x0 = Math.min(1, span);
      else if (anchorOpt === 'right') x0 = Math.max(0, span - 1);
      else if (anchorOpt === 'center') x0 = (span / 2) | 0;
      else { x0 = (rand(0, Math.max(1, span + 1)) | 0); x0 = Math.min(x0, span); }
      // build cell list; reserve top of end columns for the triangles
      var cells = [], triRCell = null, triLCell = null;
      for (var c = 0; c < wW; c++) {
        for (var r = 0; r < hs[c]; r++) {
          var cellPos = { x: x0 + c, y: yBot - r, top: r === hs[c] - 1, col: c };
          cells.push(cellPos);
        }
      }
      var hasTriR = false, hasTriL = false;
      for (var t1 = 0; t1 < st.pieces.length; t1++) {
        if (st.pieces[t1].type === 'triR') hasTriR = true;
        else if (st.pieces[t1].type === 'triL') hasTriL = true;
      }
      var rest = [];
      for (var c2 = 0; c2 < cells.length; c2++) {
        var cc = cells[c2];
        // reserve a corner slot ONLY for a triangle that actually exists in the kit —
        // otherwise the cell would be orphaned and two pieces would share a slot
        if (hasTriR && cc.top && cc.col === 0 && !triRCell) { triRCell = cc; continue; }
        if (hasTriL && cc.top && cc.col === wW - 1 && !triLCell) { triLCell = cc; continue; }
        rest.push(cc);
      }
      // assign: triangles to their corner slots; everything else by x-order (low-crossing glides)
      var movers = [], tris = [];
      for (var p = 0; p < st.pieces.length; p++) {
        (st.pieces[p].type === 'triR' || st.pieces[p].type === 'triL') ? tris.push(st.pieces[p]) : movers.push(st.pieces[p]);
      }
      for (var t2 = 0; t2 < tris.length; t2++) {
        var slot = tris[t2].type === 'triR' ? (triRCell || rest.shift()) : (triLCell || rest.shift());
        if (!slot) slot = { x: x0, y: yBot };
        setTarget(tris[t2], slot, first);
      }
      movers.sort(function (a, b) { return a.cx - b.cx || a.cy - b.cy; });
      rest.sort(function (a, b) { return a.x - b.x || a.y - b.y; });
      for (var m = 0; m < movers.length; m++) setTarget(movers[m], rest[m] || rest[rest.length - 1] || { x: x0, y: yBot }, first);
      // stagger: drop order bottom-first on intro; gentle index stagger on shuffles
      for (var s2 = 0; s2 < st.pieces.length; s2++) {
        var pc = st.pieces[s2];
        pc.k = first ? ((yBot - pc.cy) + (pc.cx - x0) * 0.22 + Math.random() * 0.1) : s2 * 0.07;
      }
      if (first) {
        var maxK = 0;
        for (var k1 = 0; k1 < st.pieces.length; k1++) if (st.pieces[k1].k > maxK) maxK = st.pieces[k1].k;
        for (var k2 = 0; k2 < st.pieces.length; k2++) st.pieces[k2].k = maxK ? st.pieces[k2].k / (maxK + 0.001) : 0;
      }
      // connector octagons: lattice corners shared by 2+ placed cells
      var cornerCount = {};
      for (var cl = 0; cl < cells.length; cl++) {
        var cx2 = cells[cl].x, cy2 = cells[cl].y;
        [[cx2, cy2], [cx2 + 1, cy2], [cx2, cy2 + 1], [cx2 + 1, cy2 + 1]].forEach(function (pt) {
          var key = pt[0] + ',' + pt[1];
          cornerCount[key] = (cornerCount[key] || 0) + 1;
        });
      }
      var newPts = [];
      for (var key2 in cornerCount) {
        if (cornerCount[key2] >= 2 && Math.random() < 0.75) {
          var xy = key2.split(',');
          newPts.push({ x: xy[0] * cell, y: xy[1] * cell });
        }
      }
      if (newPts.length > 10) newPts.length = 10;
      /* connector transitions: connectors SHUFFLE with the panels — old junctions
         are matched to new ones by position and GLIDE there; because junction
         counts change between layouts, surplus old connectors DISSOLVE out and
         brand-new ones dissolve in after the pieces settle. */
      if (first) {
        st.conns = [];
        for (var ni = 0; ni < newPts.length; ni++) {
          st.conns.push({ x: newPts[ni].x, y: newPts[ni].y, fx: newPts[ni].x, fy: newPts[ni].y, mode: 'in', k: Math.random() });
        }
      } else {
        var oldC = [];
        for (var oi = 0; oi < st.conns.length; oi++) if (st.conns[oi].mode !== 'out') oldC.push(st.conns[oi]);
        oldC.sort(function (a, b) { return a.x - b.x || a.y - b.y; });
        newPts.sort(function (a, b) { return a.x - b.x || a.y - b.y; });
        var nextC = [], paired = Math.min(oldC.length, newPts.length);
        for (var pi = 0; pi < paired; pi++) {
          nextC.push({ fx: oldC[pi].x, fy: oldC[pi].y, x: newPts[pi].x, y: newPts[pi].y, mode: 'move', k: Math.random() });
        }
        for (var ei = paired; ei < newPts.length; ei++) {
          nextC.push({ x: newPts[ei].x, y: newPts[ei].y, fx: newPts[ei].x, fy: newPts[ei].y, mode: 'in', k: Math.random() });
        }
        for (var di = paired; di < oldC.length; di++) {
          nextC.push({ x: oldC[di].x, y: oldC[di].y, fx: oldC[di].x, fy: oldC[di].y, mode: 'out', k: Math.random() });
        }
        st.conns = nextC;
      }
    }

    function setTarget(p, slot, first) {
      p.fx = first ? slot.x : p.cx;
      p.fy = first ? slot.y : p.cy;
      p.cx = slot.x; p.cy = slot.y;
      p.prog = first ? 1 : 0;
    }

    function drawCorners(x, y, w, h, alpha) {
      var d = Math.max(2.5, cell * 0.07);
      ctx.fillStyle = rgba(ink, 0.95 * alpha);
      ctx.fillRect(x - d / 2, y - d / 2, d, d);
      ctx.fillRect(x + w - d / 2, y - d / 2, d, d);
      ctx.fillRect(x - d / 2, y + h - d / 2, d, d);
      ctx.fillRect(x + w - d / 2, y + h - d / 2, d, d);
    }

    function drawPiece(p, x, y, alpha) {
      var lw = Math.max(1.5, cell * 0.045); // reads like the 4px tetris border at panel scale
      ctx.lineWidth = lw;
      ctx.strokeStyle = rgba(ink, 0.9 * alpha);
      if (p.type === 'fill') {
        ctx.fillStyle = rgba(ink, 0.20 * alpha);   // lighter-blue look on the blue field
        ctx.fillRect(x, y, cell, cell);
        ctx.strokeRect(x + lw / 2, y + lw / 2, cell - lw, cell - lw);
        drawCorners(x, y, cell, cell, alpha);
      } else if (p.type === 'grid') {
        ctx.strokeRect(x + lw / 2, y + lw / 2, cell - lw, cell - lw);
        ctx.strokeStyle = rgba(ink, 0.32 * alpha); // 4x4 inner wire grid
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (var i = 1; i < 4; i++) {
          ctx.moveTo(x + (cell / 4) * i + .5, y); ctx.lineTo(x + (cell / 4) * i + .5, y + cell);
          ctx.moveTo(x, y + (cell / 4) * i + .5); ctx.lineTo(x + cell, y + (cell / 4) * i + .5);
        }
        ctx.stroke();
        drawCorners(x, y, cell, cell, alpha);
      } else if (p.type === 'half') {
        var hh = cell / 2;                         // two stacked half panels: top open, bottom filled
        ctx.strokeRect(x + lw / 2, y + lw / 2, cell - lw, hh - lw);
        ctx.fillStyle = rgba(ink, 0.20 * alpha);
        ctx.fillRect(x, y + hh, cell, hh);
        ctx.strokeRect(x + lw / 2, y + hh + lw / 2, cell - lw, hh - lw);
        drawCorners(x, y, cell, hh, alpha);
        drawCorners(x, y + hh, cell, hh, alpha);
      } else { // triR / triL — top-corner triangles, hypotenuse outward
        ctx.fillStyle = rgba(ink, 0.20 * alpha);
        ctx.beginPath();
        if (p.type === 'triR') { // left end of the top row: rises to the right
          ctx.moveTo(x, y + cell); ctx.lineTo(x + cell, y + cell); ctx.lineTo(x + cell, y);
        } else {                 // right end: rises to the left
          ctx.moveTo(x, y); ctx.lineTo(x, y + cell); ctx.lineTo(x + cell, y + cell);
        }
        ctx.closePath(); ctx.fill(); ctx.stroke();
        var d = Math.max(2.5, cell * 0.07);
        ctx.fillStyle = rgba(ink, 0.95 * alpha);
        ctx.fillRect(x - d / 2 + (p.type === 'triL' ? 0 : cell), y - d / 2, d, d);
        ctx.fillRect(x - d / 2, y + cell - d / 2, d, d);
        ctx.fillRect(x + cell - d / 2, y + cell - d / 2, d, d);
      }
    }

    function drawOct(x, y, r, alpha) {
      ctx.fillStyle = rgba(ink, 0.95 * alpha);
      ctx.beginPath();
      for (var i = 0; i < 8; i++) {
        var a = (Math.PI / 4) * i + Math.PI / 8;
        var px2 = x + Math.cos(a) * r, py2 = y + Math.sin(a) * r;
        i === 0 ? ctx.moveTo(px2, py2) : ctx.lineTo(px2, py2);
      }
      ctx.closePath(); ctx.fill();
    }

    function drawStructures(dtMs) {
      var dt = dtMs * speed;
      for (var s = 0; s < structures.length; s++) {
        var st = structures[s];

        /* ── state machine: drop → pop → idle → glide → pop → idle → … ── */
        var sp = scrollBuild ? scrollP() : 0;
        if (!isStatic) {
          st.clock += dt;
          if (st.state === 'drop' && (scrollBuild ? sp >= 1 : st.clock >= st.dropDur)) { st.state = 'pop'; st.clock = 0; }
          else if (st.state === 'pop' && st.clock >= st.popDur) {
            st.state = 'idle'; st.clock = 0; st.connP = 1; st.accP = 1; st.accShown = true;
            // settle connectors: drop the dissolved-out ones, lock in the rest
            var keep = [];
            for (var ki = 0; ki < st.conns.length; ki++) {
              if (st.conns[ki].mode !== 'out') { st.conns[ki].mode = 'set'; keep.push(st.conns[ki]); }
            }
            st.conns = keep;
          }
          else if (st.state === 'idle' && st.clock >= Math.max(600, st.interval - st.glideDur - st.popDur)) {
            arrange(st, false);                       // same pieces → new setup (+ connector transitions)
            st.state = 'glide'; st.clock = 0;
          }
          else if (st.state === 'glide') {
            var lastDelay = (st.pieces.length - 1) * 0.07 * 1000;
            if (st.clock >= st.glideDur + lastDelay) { st.state = 'pop'; st.clock = 0; }
          }
          if (st.state === 'pop') st.connP = st.accP = Math.min(1, st.clock / st.popDur);
        }

        /* ── pieces (current draw position is captured so accessories can ride along) ── */
        for (var p = 0; p < st.pieces.length; p++) {
          var pc = st.pieces[p];
          pc._dx = pc.cx * cell; pc._dy = pc.cy * cell;
          if (st.state === 'drop' && !isStatic) {
            // intro: tetris drop, bottom row first. With scroll-build the build is
            // SCRUBBED by scroll position (reverses if the visitor scrolls back up,
            // and back down again) until it completes and hands off.
            var win = 0.45, p0 = pc.k * (1 - win);
            var tt = scrollBuild ? sp : st.clock / st.dropDur;
            var pr = tt <= p0 ? 0 : (tt >= p0 + win ? 1 : easeInOut((tt - p0) / win));
            if (pr <= 0) continue;
            pc._dy = pc.cy * cell - (1 - pr) * cell * 2.4;
            drawPiece(pc, pc._dx, pc._dy, Math.min(1, pr * 1.5));
          } else if (st.state === 'glide' && !isStatic) {
            // fluid 2D shuffle: glide from old cell to new cell, staggered
            var d0 = pc.k * 1000;
            var gp = st.clock <= d0 ? 0 : Math.min(1, (st.clock - d0) / st.glideDur);
            var e = easeInOut(gp);
            pc._dx = (pc.fx + (pc.cx - pc.fx) * e) * cell;
            pc._dy = (pc.fy + (pc.cy - pc.fy) * e) * cell;
            drawPiece(pc, pc._dx, pc._dy, 1);
          } else {
            drawPiece(pc, pc._dx, pc._dy, 1);
          }
        }

        /* ── connector octagons: glide with the shuffle (matched junctions),
              dissolve out/in where the junction count changes, and fade in
              GRADUALLY (long stagger + ease + grow) after each settle ── */
        var cr = Math.max(3, cell * 0.10);
        for (var c3 = 0; c3 < st.conns.length; c3++) {
          var cn = st.conns[c3];
          var ca = 1, cpx = cn.x, cpy = cn.y, rscale = 1;
          if (st.state === 'drop') {
            ca = 0;
          } else if (st.state === 'glide' && !isStatic) {
            if (cn.mode === 'move') {
              var cgp = Math.max(0, Math.min(1, (st.clock - cn.k * 240) / st.glideDur));
              var cge = easeInOut(cgp);
              cpx = cn.fx + (cn.x - cn.fx) * cge;
              cpy = cn.fy + (cn.y - cn.fy) * cge;
            } else if (cn.mode === 'out') {
              ca = Math.max(0, 1 - st.clock / (st.glideDur * 0.55)); // dissolve away
              rscale = 0.4 + 0.6 * ca;
            } else { ca = 0; } // 'in' arrives gradually with the next pop
          } else if (st.state === 'pop' && !isStatic) {
            if (cn.mode === 'in') {
              ca = easeInOut(Math.max(0, Math.min(1, ((st.connP || 0) - cn.k * 0.55) / 0.45)));
              rscale = ca;
            } else if (cn.mode === 'out') { ca = 0; }
          } else { // idle / static
            if (cn.mode === 'out') ca = 0;
          }
          if (ca > 0.01) drawOct(cpx, cpy, cr * (0.35 + 0.65 * rscale), ca);
        }

        /* ── hung accessories: PERSISTENT — they ride their host panel through
              every shuffle; only the very first reveal fades in gradually ── */
        if (st.acc.length && (st.accShown || st.state === 'pop')) {
          var sq2 = Math.max(4, cell * 0.14);
          for (var a = 0; a < st.acc.length; a++) {
            var ac = st.acc[a];
            var host = st.pieces[ac.piece];
            var hx = host._dx !== undefined ? host._dx : host.cx * cell;
            var hy = host._dy !== undefined ? host._dy : host.cy * cell;
            var aa = 1, dropOff = 0;
            if (!st.accShown) { // first gradual reveal (slow stagger + small drop)
              aa = easeInOut(Math.max(0, Math.min(1, ((st.accP || 0) - ac.k * 0.55) / 0.45)));
              if (aa <= 0.01) continue;
              dropOff = (1 - aa) * 6;
            }
            ctx.fillStyle = rgba(ink, 0.9 * aa);
            ctx.fillRect(hx + (cell / 4) * ac.ix - sq2 / 2, hy + (cell / 4) * ac.iy - sq2 / 2 - dropOff, sq2, sq2);
          }
        }
      }
    }

    function newCross(mid) {
      return { cx: (rand(1, cols - 1) | 0), cy: (rand(1, rows - 1) | 0),
               t: mid ? rand(0, 1) : 0, dur: rand(2600, 5200) / speed };
    }
    function newSquare(mid) {
      var cx = (rand(0, cols - 1) | 0), cy = (rand(0, rows - 1) | 0);
      return { cx: cx, cy: cy, px: cx, py: cy, hop: mid ? rand(0, 1) : 0,
               hopDur: rand(1800, 3000) / speed, life: mid ? rand(0, .5) : 0,
               lifeDur: rand(14000, 22000) / speed };
    }
    function newMorph(mid) {
      return { cx: (rand(0, cols - 2) | 0), cy: (rand(0, rows - 2) | 0),
               t: mid ? rand(0, .4) : 0, dur: rand(7000, 9500) / speed,
               horiz: Math.random() > 0.5 };
    }
    function newLineDraw() {
      var horiz = Math.random() > 0.5;
      return { cx: (rand(0, cols - 1) | 0), cy: (rand(0, rows - 1) | 0),
               horiz: horiz, t: 0, dur: rand(1600, 2600) / speed };
    }

    var gridLayer = null;
    function buildGridLayer() {
      gridLayer = document.createElement('canvas');
      gridLayer.width = cv.width; gridLayer.height = cv.height;
      var g = gridLayer.getContext('2d');
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.fillStyle = rgba(bg, 1);
      g.fillRect(0, 0, W, H);
      g.strokeStyle = rgba(ink, 0.14);
      g.lineWidth = 1;
      g.beginPath();
      for (var x = 0; x <= cols; x++) { g.moveTo(x * cell + .5, 0); g.lineTo(x * cell + .5, H); }
      for (var y = 0; y <= rows; y++) { g.moveTo(0, y * cell + .5); g.lineTo(W, y * cell + .5); }
      g.stroke();
    }

    function drawCross(x, y, a) {
      var s = 5;
      ctx.strokeStyle = rgba(ink, a);
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(x - s, y); ctx.lineTo(x + s, y);
      ctx.moveTo(x, y - s); ctx.lineTo(x, y + s);
      ctx.stroke();
    }

    function drawMatrix(x, y, size, n, prog, a) {
      // n x n dot matrix centred in a cell, dots appear one by one with `prog` 0..1
      var gap = size / (n + 1), d = Math.max(2.5, size / 14);
      var total = n * n, shown = Math.ceil(prog * total);
      ctx.fillStyle = rgba(ink, a);
      for (var i = 0; i < shown; i++) {
        var gx = i % n, gy = (i / n) | 0;
        ctx.fillRect(x + gap * (gx + 1) - d / 2, y + gap * (gy + 1) - d / 2, d, d);
      }
    }

    function draw(dtMs) {
      // bg + grid are pre-rendered once per resize — blitting the layer is far
      // cheaper than re-stroking ~30 lines every frame (battery + jank)
      if (gridLayer) ctx.drawImage(gridLayer, 0, 0, W, H);
      else { ctx.fillStyle = rgba(bg, 1); ctx.fillRect(0, 0, W, H); }

      // "+" intersections — slow pulse
      for (var i = 0; i < crosses.length; i++) {
        var c = crosses[i];
        var p = c.t % 1;
        var a = p < 0.5 ? easeInOut(p * 2) : easeInOut((1 - p) * 2);
        drawCross(c.cx * cell, c.cy * cell, 0.18 + a * 0.55);
        if (!isStatic) { c.t += dtMs / c.dur; if (c.t >= 1) crosses[i] = newCross(false); }
      }

      if (mode === 'stack') { drawStructures(dtMs); return; }

      // lone hopping squares
      var sq = Math.max(8, cell * 0.13);
      for (var j = 0; j < squares.length; j++) {
        var s = squares[j];
        var hp = easeInOut(Math.min(1, s.hop));
        var x = (s.px + (s.cx - s.px) * hp + 0.5) * cell - sq / 2;
        var y = (s.py + (s.cy - s.py) * hp + 0.5) * cell - sq / 2;
        var lf = s.life < 0.12 ? s.life / 0.12 : (s.life > 0.88 ? (1 - s.life) / 0.12 : 1);
        ctx.fillStyle = rgba(ink, 0.30 * lf);
        ctx.fillRect(x, y, sq, sq);
        if (!isStatic) {
          s.hop += dtMs / s.hopDur; s.life += dtMs / s.lifeDur;
          if (s.hop >= 1.6) { // dwell after arriving, then hop again
            s.px = s.cx; s.py = s.cy;
            var dir = pick([[1, 0], [-1, 0], [0, 1], [0, -1]]);
            s.cx = Math.max(0, Math.min(cols - 1, s.cx + dir[0]));
            s.cy = Math.max(0, Math.min(rows - 1, s.cy + dir[1]));
            s.hop = 0; s.hopDur = rand(1800, 3000) / speed;
          }
          if (s.life >= 1) squares[j] = newSquare(false);
        }
      }

      // signature morph: dot → line → square outline → 3x3 matrix → dissolve
      for (var k = 0; k < morphs.length; k++) {
        var m = morphs[k];
        var mx = m.cx * cell, my = m.cy * cell, size = cell;
        var ph = m.t;
        ctx.strokeStyle = rgba(ink, 0.8); ctx.lineWidth = 1.4;
        if (ph < 0.12) { // dot grows in
          var a0 = ph / 0.12;
          ctx.fillStyle = rgba(ink, 0.8 * a0);
          var d0 = 3 * a0;
          ctx.fillRect(mx + size / 2 - d0 / 2, my + size / 2 - d0 / 2, d0, d0);
        } else if (ph < 0.30) { // dot extends into a line
          var p1 = easeInOut((ph - 0.12) / 0.18);
          ctx.beginPath();
          if (m.horiz) { ctx.moveTo(mx + size / 2 - (size / 2) * p1, my + size / 2); ctx.lineTo(mx + size / 2 + (size / 2) * p1, my + size / 2); }
          else { ctx.moveTo(mx + size / 2, my + size / 2 - (size / 2) * p1); ctx.lineTo(mx + size / 2, my + size / 2 + (size / 2) * p1); }
          ctx.stroke();
        } else if (ph < 0.52) { // line sweeps into a square outline
          var p2 = easeInOut((ph - 0.30) / 0.22);
          var half = size / 2, off = half * p2;
          ctx.beginPath();
          if (m.horiz) { ctx.strokeRect(mx + .5, my + half - off + .5, size - 1, off * 2 - 1 || 1); }
          else { ctx.strokeRect(mx + half - off + .5, my + .5, off * 2 - 1 || 1, size - 1); }
        } else if (ph < 0.62) { // square holds
          ctx.strokeRect(mx + .5, my + .5, size - 1, size - 1);
        } else if (ph < 0.86) { // subdivides into a 3x3 dot matrix
          var p3 = (ph - 0.62) / 0.24;
          ctx.strokeStyle = rgba(ink, 0.8 * (1 - p3));
          ctx.strokeRect(mx + .5, my + .5, size - 1, size - 1);
          drawMatrix(mx, my, size, 3, Math.min(1, p3 * 1.4), 0.75);
        } else { // matrix dissolves
          var p4 = (ph - 0.86) / 0.14;
          drawMatrix(mx, my, size, 3, 1, 0.75 * (1 - p4));
        }
        if (!isStatic) { m.t += dtMs / m.dur; if (m.t >= 1) morphs[k] = newMorph(false); }
      }

      // occasional grid-edge line draws
      if (!isStatic && lineDraws.length < 2 && Math.random() < 0.004 * density * (dtMs / 16.7)) lineDraws.push(newLineDraw());
      for (var l = lineDraws.length - 1; l >= 0; l--) {
        var ld = lineDraws[l];
        var lp = ld.t < 0.6 ? easeInOut(ld.t / 0.6) : 1;
        var la = ld.t < 0.6 ? 0.7 : 0.7 * (1 - (ld.t - 0.6) / 0.4);
        ctx.strokeStyle = rgba(ink, la); ctx.lineWidth = 1.4;
        ctx.beginPath();
        if (ld.horiz) { ctx.moveTo(ld.cx * cell, ld.cy * cell + .5); ctx.lineTo(ld.cx * cell + cell * lp, ld.cy * cell + .5); }
        else { ctx.moveTo(ld.cx * cell + .5, ld.cy * cell); ctx.lineTo(ld.cx * cell + .5, ld.cy * cell + cell * lp); }
        ctx.stroke();
        if (!isStatic) { ld.t += dtMs / ld.dur; if (ld.t >= 1) lineDraws.splice(l, 1); }
      }
    }

    var lastT = 0;
    function loop(now) {
      if (!running) return;
      var dtMs = lastT ? Math.min(50, now - lastT) : 16.7;
      lastT = now;
      draw(dtMs);
      raf = requestAnimationFrame(loop);
    }
    function start() { if (running || isStatic) return; running = true; lastT = 0; raf = requestAnimationFrame(loop); }
    function stop() { running = false; if (raf) cancelAnimationFrame(raf); }

    resize();
    var rsTick;
    function queueResize() { clearTimeout(rsTick); rsTick = setTimeout(resize, 180); }
    window.addEventListener('resize', queueResize, { passive: true });
    // self-heal: containers that are 0-wide at load (hidden tabs, late layout)
    if (window.ResizeObserver) new ResizeObserver(queueResize).observe(cv.parentElement);

    if (isStatic) { draw(0); return; }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        es.forEach(function (e) { visible = e.isIntersecting; visible ? start() : stop(); });
      }, { rootMargin: '120px 0px' }).observe(cv);
    } else { start(); }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else if (visible) start();
    });
  }

  /* ── LOTTIE TILES ──
     Bootstraps [data-ab-lottie] elements (bento tiles). Lives here — not in
     inline section scripts — because the theme editor patches section HTML
     without executing inline <script>; this file's MutationObserver +
     shopify:section:load hooks catch tiles added/changed in the editor.
     The 164KB player lib (self-hosted, data-lib URL) loads once, lazily. */
  function ensureLottieLib(src, cb) {
    if (window.lottie) return cb();
    if (window.__abLottieLoading) { document.addEventListener('ab:lottie-ready', cb, { once: true }); return; }
    window.__abLottieLoading = true;
    var s = document.createElement('script');
    s.src = src;
    s.onload = function () { document.dispatchEvent(new CustomEvent('ab:lottie-ready')); cb(); };
    (document.head || document.body).appendChild(s);
  }

  function initLottie(el) {
    el.dataset.abLtInit = '1';
    var boot = function () {
      ensureLottieLib(el.dataset.lib, function () {
        var anim = window.lottie.loadAnimation({
          container: el, renderer: 'svg',
          loop: el.dataset.loop !== 'false',
          autoplay: false,
          path: el.dataset.abLottie
        });
        var baseSpeed = parseFloat(el.dataset.speed) || 1;
        anim.setSpeed(baseSpeed);
        /* hover = 2.5x playback (bento tiles): pointer-fine only, never under
           reduced motion. NOTE: invisible in the theme editor by design — tiles
           hold a still frame there (see DOMLoaded below); test on the storefront. */
        if (!REDUCE && !EDITOR && window.matchMedia && window.matchMedia('(hover:hover) and (pointer:fine)').matches) {
          var hoverHost = el.closest('.abbg__tile') || el;
          hoverHost.addEventListener('mouseenter', function () { anim.setSpeed(baseSpeed * 2.5); });
          hoverHost.addEventListener('mouseleave', function () { anim.setSpeed(baseSpeed); });
        }
        anim.addEventListener('DOMLoaded', function () {
          if (REDUCE || EDITOR) {
            // still frame for editor + reduced motion — MIDDLE frame, not frame 0
            // (many animations, e.g. scale-ups, are empty at frame 0)
            anim.goToAndStop(Math.max(1, Math.floor(anim.totalFrames / 2)), true);
            return;
          }
          if ('IntersectionObserver' in window) {
            new IntersectionObserver(function (es) {
              es.forEach(function (e) { e.isIntersecting ? anim.play() : anim.pause(); });
            }, { rootMargin: '80px 0px' }).observe(el);
          } else { anim.play(); }
        });
      });
    };
    if ('IntersectionObserver' in window && !EDITOR) {
      /* already in (or near) the viewport at init: boot NOW - IO callbacks can be
         deferred indefinitely in throttled/background tabs, and a tile that starts
         on-screen shouldn't wait for one. */
      var r0 = el.getBoundingClientRect();
      if (r0.top < (window.innerHeight || 800) + 300 && r0.bottom > -300) { boot(); }
      else {
        // fetch lib + JSON only when the tile nears the viewport
        var io = new IntersectionObserver(function (es) {
          es.forEach(function (e) { if (e.isIntersecting) { boot(); io.disconnect(); } });
        }, { rootMargin: '300px 0px' });
        io.observe(el);
      }
    } else { boot(); } // editor: boot now so the merchant sees the still immediately
  }

  function initAll() {
    document.querySelectorAll('canvas[data-ab-modgrid]:not([data-ab-init])').forEach(setup);
    document.querySelectorAll('[data-ab-lottie]:not([data-ab-lt-init])').forEach(initLottie);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  // late-rendered sections (editor reloads, dynamic content)
  if (window.MutationObserver) {
    /* only when an added node is (or holds) a grid canvas or a Lottie tile: a whole-document scan on every DOM change
       (a click ripple, a prefetch link) cost about 90 ms at 4x CPU on the homepage */
    var Q = 'canvas[data-ab-modgrid],[data-ab-lottie]';
    new MutationObserver(function (recs) {
      for (var i = 0; i < recs.length; i++) {
        var a = recs[i].addedNodes;
        for (var j = 0; j < a.length; j++) { var n = a[j]; if (n.nodeType === 1 && (n.matches(Q) || n.querySelector(Q))) { initAll(); return; } }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
  document.addEventListener('shopify:section:load', initAll);
})();
